import { PATH_ROOT, PATH_SENTRY_PLAYGROUND } from "@/constants";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { useMemo } from "react";

export default function SentryPlayground() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("sentryPlayground.title"), href: PATH_SENTRY_PLAYGROUND },
    ],
    [t],
  );

  const showReportedToast = () => {
    toast({
      title: t("sentryPlayground.reportedToastTitle"),
      description: t("sentryPlayground.reportedToastDescription"),
    });
  };

  const triggerSyncError = () => {
    showReportedToast();
    throw new Error("[Sentry Playground] Synchronous error thrown");
  };

  const triggerAsyncError = () => {
    showReportedToast();
    Promise.reject(
      new Error("[Sentry Playground] Unhandled promise rejection"),
    );
  };

  const triggerCaptureException = () => {
    Sentry.captureException(
      new Error("[Sentry Playground] Sentry.captureException"),
    );
    showReportedToast();
  };

  const triggerStringRejection = () => {
    Promise.reject("[Sentry Playground] String rejection (non-Error)");
    toast({
      title: t("sentryPlayground.stringRejectionToastTitle"),
      description: t("sentryPlayground.stringRejectionToastDescription"),
    });
  };

  const triggerCaptureMessage = () => {
    Sentry.captureMessage("[Sentry Playground] Manual message", "warning");
    showReportedToast();
  };

  return (
    <>
      <PageHeader
        breadcrumbItems={breadcrumbItems}
        title={t("sentryPlayground.title")}
      />
      <div className="space-y-6 p-4">
        <Alert variant="destructive">
          <AlertTitle>{t("sentryPlayground.warningTitle")}</AlertTitle>
          <AlertDescription>
            {t("sentryPlayground.warningDescription")}
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t("sentryPlayground.triggersTitle")}
          </h2>
          <div className="flex flex-wrap gap-2">
            <Button variant="destructive" onClick={triggerSyncError}>
              {t("sentryPlayground.triggerSyncError")}
            </Button>
            <Button variant="destructive" onClick={triggerAsyncError}>
              {t("sentryPlayground.triggerAsyncError")}
            </Button>
            <Button variant="destructive" onClick={triggerCaptureException}>
              {t("sentryPlayground.triggerCaptureException")}
            </Button>
            <Button variant="destructive" onClick={triggerStringRejection}>
              {t("sentryPlayground.triggerStringRejection")}
            </Button>
            <Button variant="outline" onClick={triggerCaptureMessage}>
              {t("sentryPlayground.triggerCaptureMessage")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
